(function () {
  /**
   * @typedef {HTMLElement & { disabled?: boolean }} TargetElement
   */
  /**
   * @typedef {{ target: TargetElement, timestamp: number }} TriggerState
   */
  /**
   * @typedef {{ count: number, disabled: boolean, wasDisabled: boolean, previousAriaBusy: string | null }} TargetLoadingState
   */
  /**
   * @typedef {{ target: TargetElement } | null} ActiveLoadingState
   */

  if (window.__amRequestLoadingInitialized) return;
  window.__amRequestLoadingInitialized = true;

  const TRIGGER_MAX_AGE = 1500;
  const IGNORE_URL_PATTERNS = [/save-wishlist/i];
  const WISHLIST_SELECTOR = [
    '.wishlist-engine',
    '.wishlist-engine-button',
    '.wishlist-icon',
    '.custom-btn-wishlist',
    '.custom-btn-wishlist-mobile',
    '[data-wishlist-toggle]',
  ].join(',');
  const CART_CONTROL_SELECTOR = [
    '.cart-items-component .quantity-minus',
    '.cart-items-component .quantity-plus',
    '.cart-items-component .cart-items__remove',
  ].join(',');
  const TAB_CONTROL_SELECTOR = [
    '.categories-tabs__nav button',
    '.categories-tabs__nav [data-tab-button]',
    'button.tab-button',
    '.tab-buttons .tab-button',
  ].join(',');
  const TARGET_SELECTOR = [
    'button',
    'input[type="submit"]',
    'input[type="button"]',
    'a.button',
    'a.button-secondary',
    '.button',
    '.button-secondary',
    '[role="button"]',
    '.js-add-to-cart',
  ].join(',');

  /** @type {WeakMap<TargetElement, TargetLoadingState>} */
  const targetState = new WeakMap();
  /** @type {TriggerState | null} */
  let lastTrigger = null;

  /**
   * @param {unknown} value
   * @returns {value is Element}
   */
  function isElement(value) {
    return value instanceof Element;
  }

  /**
   * @param {Element} element
   * @returns {boolean}
   */
  function isTextInput(element) {
    if (!(element instanceof HTMLInputElement)) return false;
    const type = (element.type || '').toLowerCase();
    return ['button', 'submit', 'reset', 'checkbox', 'radio', 'file', 'image'].includes(type) === false;
  }

  /**
   * @param {unknown} element
   * @returns {TargetElement | null}
   */
  function findTarget(element) {
    if (!isElement(element)) return null;

    if (element.matches(WISHLIST_SELECTOR) || element.closest(WISHLIST_SELECTOR)) {
      return null;
    }

    if (element.matches(CART_CONTROL_SELECTOR) || element.closest(CART_CONTROL_SELECTOR)) {
      return null;
    }

    if (element.matches(TAB_CONTROL_SELECTOR) || element.closest(TAB_CONTROL_SELECTOR)) {
      return null;
    }

    if (element.matches(TARGET_SELECTOR)) {
      return element instanceof HTMLElement ? element : null;
    }

    const closest = element.closest(TARGET_SELECTOR);
    return closest instanceof HTMLElement ? closest : null;
  }

  /**
   * @param {unknown} source
   * @param {unknown} [explicitTarget]
   * @returns {void}
   */
  function rememberTrigger(source, explicitTarget) {
    const rawTarget = explicitTarget || source;
    if (isElement(rawTarget) && (rawTarget.matches(WISHLIST_SELECTOR) || rawTarget.closest(WISHLIST_SELECTOR))) {
      lastTrigger = null;
      return;
    }

    const target = findTarget(rawTarget);
    if (!target) return;

    lastTrigger = {
      target,
      timestamp: Date.now(),
    };
  }

  /**
   * @param {unknown} input
   * @returns {string}
   */
  function getRequestUrl(input) {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.toString();
    if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
    if (input && typeof input === 'object') {
      const requestLike = /** @type {{ url?: unknown }} */ (input);
      if (typeof requestLike.url === 'string') return requestLike.url;
    }
    return '';
  }

  /**
   * @param {unknown} input
   * @returns {boolean}
   */
  function shouldIgnoreRequest(input) {
    const url = getRequestUrl(input);
    if (!url) return false;
    return IGNORE_URL_PATTERNS.some((pattern) => pattern.test(url));
  }

  /**
   * @param {unknown} init
   * @returns {boolean}
   */
  function shouldIgnoreInit(init) {
    if (!init || typeof init !== 'object') return false;
    const requestInitLike = /** @type {{ __skipRequestLoading?: unknown }} */ (init);
    return Boolean(requestInitLike.__skipRequestLoading);
  }

  /**
   * @returns {TargetElement | null}
   */
  function getRecentTrigger() {
    if (!lastTrigger) return null;
    if (Date.now() - lastTrigger.timestamp > TRIGGER_MAX_AGE) {
      lastTrigger = null;
      return null;
    }

    if (!document.contains(lastTrigger.target)) {
      lastTrigger = null;
      return null;
    }

    return lastTrigger.target;
  }

  /**
   * @param {TargetElement} target
   * @returns {TargetLoadingState}
   */
  function ensureTargetState(target) {
    let state = targetState.get(target);
    if (state) return state;

    state = {
      count: 0,
      disabled: target instanceof HTMLButtonElement || (target instanceof HTMLInputElement && !isTextInput(target)),
      wasDisabled: false,
      previousAriaBusy: target.getAttribute('aria-busy'),
    };

    targetState.set(target, state);
    return state;
  }

  /**
   * @param {TargetElement | null} target
   * @returns {ActiveLoadingState}
   */
  function beginTargetLoading(target) {
    if (!target) {
      return null;
    }

    const state = ensureTargetState(target);
    state.count += 1;

    if (state.count > 1) return { target };

    target.classList.add('request-loading-target');
    target.setAttribute('aria-busy', 'true');

    if (state.disabled) {
      state.wasDisabled = Boolean(target.disabled);
      target.disabled = true;
    }

    return { target };
  }

  /**
   * @param {ActiveLoadingState} loadingState
   * @returns {void}
   */
  function endTargetLoading(loadingState) {
    const target = loadingState?.target;
    if (!target) return;

    const state = targetState.get(target);
    if (!state) return;

    state.count = Math.max(0, state.count - 1);
    if (state.count > 0) return;

    target.classList.remove('request-loading-target');

    if (state.previousAriaBusy === null) target.removeAttribute('aria-busy');
    else target.setAttribute('aria-busy', state.previousAriaBusy);

    if (state.disabled) {
      target.disabled = state.wasDisabled;
    }
  }

  /**
   * @param {() => unknown} run
   * @param {TargetElement | null} target
   * @returns {unknown}
   */
  function trackRequest(run, target) {
    const trackedState = beginTargetLoading(target);

    try {
      const result = run();

      const resultWithFinally = /** @type {{ finally?: (onFinally: () => void) => unknown } | null | undefined} */ (result);
      if (!resultWithFinally || typeof resultWithFinally.finally !== 'function') {
        endTargetLoading(trackedState);
        return result;
      }

      return resultWithFinally.finally(() => {
        endTargetLoading(trackedState);
      });
    } catch (error) {
      endTargetLoading(trackedState);
      throw error;
    }
  }

  /**
   * @param {RequestInit & { __loadingTarget?: unknown } | undefined} init
   * @returns {TargetElement | null}
   */
  function resolveTargetFromInit(init) {
    if (!init || typeof init !== 'object') return getRecentTrigger();

    const candidate = init.__loadingTarget;
    if (isElement(candidate)) return findTarget(candidate);

    return getRecentTrigger();
  }

  document.addEventListener(
    'click',
    (event) => {
      rememberTrigger(event.target);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter instanceof Element ? event.submitter : null;
      rememberTrigger(event.target, submitter || event.target);
    },
    true
  );

  if (typeof window.fetch === 'function') {
    const nativeFetch = window.fetch.bind(window);

    /** @type {typeof window.fetch} */
    window.fetch = function patchedFetch(input, init) {
      if (shouldIgnoreRequest(input) || shouldIgnoreInit(init)) {
        return nativeFetch(input, init);
      }

      const target = resolveTargetFromInit(init);
      return trackRequest(() => nativeFetch(input, init), target);
    };
  }

  if (typeof window.XMLHttpRequest === 'function') {
    const NativeXHR = window.XMLHttpRequest;

    /**
     * @this {XMLHttpRequest}
     * @returns {XMLHttpRequest}
     */
    function PatchedXHR() {
      const xhr = new NativeXHR();
      const target = getRecentTrigger();
      let shouldIgnore = false;
      /** @type {ActiveLoadingState} */
      let loadingState = null;

      const finalize = () => {
        xhr.removeEventListener('loadend', finalize);
        if (!shouldIgnore) {
          endTargetLoading(loadingState);
        }
      };

      const nativeOpen = xhr.open;
      /**
       * @param {string} method
       * @param {string | URL} url
       * @param {boolean} [async]
       * @param {string | null} [username]
       * @param {string | null} [password]
       * @returns {void}
       */
      const patchedOpen = function patchedOpen(method, url, async, username, password) {
        shouldIgnore = shouldIgnoreRequest(url);
        return nativeOpen.call(xhr, method, url, async, username, password);
      };
      xhr.open = /** @type {XMLHttpRequest['open']} */ (/** @type {unknown} */ (patchedOpen));

      const nativeSend = xhr.send;
      /** @type {XMLHttpRequest['send']} */
      xhr.send = function patchedSend(body) {
        if (!shouldIgnore) {
          loadingState = beginTargetLoading(target);
        }
        xhr.addEventListener('loadend', finalize, { once: true });
        return nativeSend.call(xhr, body);
      };

      return xhr;
    }

    PatchedXHR.prototype = NativeXHR.prototype;
    window.XMLHttpRequest = /** @type {typeof window.XMLHttpRequest} */ (/** @type {unknown} */ (PatchedXHR));
  }
})();
